import {
    Component,
    EventEmitter,
    forwardRef,
    Input,
    Output,
} from '@angular/core'
import {
    ControlValueAccessor,
    FormControl,
    FormsModule,
    NG_VALUE_ACCESSOR,
    ReactiveFormsModule,
} from '@angular/forms'
import {
    debounceTime,
    distinctUntilChanged,
    Observable,
    of,
    switchMap,
    tap,
} from 'rxjs'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatInputModule } from '@angular/material/input'
import { MatAutocompleteModule } from '@angular/material/autocomplete'
import { MatStepperModule } from '@angular/material/stepper'
import { MatIconModule } from '@angular/material/icon'
import { MatCheckboxModule } from '@angular/material/checkbox'
import { MatRadioModule } from '@angular/material/radio'
import { MatSelectModule } from '@angular/material/select'
import { MatDatepickerModule } from '@angular/material/datepicker'
import { MatNativeDateModule } from '@angular/material/core'
import { NgFor, NgIf } from '@angular/common'
import { MatProgressSpinner } from '@angular/material/progress-spinner'

@Component({
    selector: 'app-search-select',
    standalone: true,
    imports: [
        MatStepperModule,
        FormsModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatIconModule,
        MatCheckboxModule,
        MatRadioModule,
        MatSelectModule,
        MatDatepickerModule,
        MatNativeDateModule,
        NgIf,
        NgFor,
        MatAutocompleteModule,
        MatProgressSpinner,
    ],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SearchSelectComponent),
            multi: true,
        },
    ],
    templateUrl: './search-select.component.html',
    styleUrl: './search-select.component.css',
})
export class SearchSelectComponent<T> implements ControlValueAccessor {
    @Input() label: string = ''
    @Input() placeholder: string = ''

    /** Función de búsqueda inyectada desde el padre */
    @Input() searchFn: (value: string) => Observable<T[]> = () => of([])

    /** Lista precargada local */
    @Input() preloadedList: T[] = []

    /** Función que define cómo mostrar el ítem en el input */
    @Input() displayFnFn: (item: T) => string | number = () => ''

    /** Función para determinar qué valor mostrar cuando el ítem es seleccionado */
    @Input() identifierFn: (item: T) => string | number | T = () => ''
    @Input() returnObject: boolean = false

    @Output() itemSeleccionado = new EventEmitter<T>()
    @Input() disabled: boolean = false

    searchControl = new FormControl<T | string | number | null>(null)
    items: T[] = []
    loading: boolean = false

    private onChange: (value: any) => void = () => {}
    private onTouched = () => {}
    private lastEmittedValue: any = null

    ngOnInit(): void {
        this.searchControl.valueChanges
            .pipe(
                debounceTime(500),
                distinctUntilChanged(),
                tap(() => {
                    this.loading = true
                    this.items = []
                }),
                switchMap((value) => {
                    if (typeof value === 'string' && value.length > 0) {
                        if (this.preloadedList.length > 0) {
                            return this.filterPreloadedList(value)
                        } else if (value.length > 4) {
                            return this.searchFn(value)
                        }
                    }
                    this.loading = false
                    return of([])
                })
            )
            .subscribe((result) => {
                this.items = Array.isArray(result)
                    ? result
                    : result && typeof result === 'object'
                    ? [result]
                    : []
                this.loading = false
            })
    }

    /** Filtrar lista local */
    private filterPreloadedList(value: string): Observable<T[]> {
        const search = value.toLowerCase()
        const filtered = this.preloadedList.filter((item) =>
            String(this.displayFnFn(item)).toLowerCase().includes(search)
        )
        return of(filtered)
    }

    displayFn = (item: T | string | number | null): string => {
        if (item == null) {
            return ''
        }
        if (typeof item === 'object') {
            return String(this.displayFnFn(item as T) ?? '')
        }
        return String(item)
    }

    onSelect(item: T): void {
        this.itemSeleccionado.emit(item)
        const valueToEmit = this.returnObject ? item : this.identifierFn(item)
        this.searchControl.setValue(item)
        this.onChange(valueToEmit)
        this.onTouched()
    }

    onBlur(): void {
        const value = this.searchControl.value
        if (typeof value === 'string' || typeof value === 'number') {
            this.onChange(value)
            this.itemSeleccionado.emit(value as any)
        }
        this.onTouched()
    }

    noResults(): boolean {
        return (
            !this.loading &&
            this.items?.length === 0 &&
            typeof this.searchControl.value === 'string' &&
            this.searchControl.value.length >
                (this.preloadedList.length > 0 ? 0 : 4)
        )
    }

    writeValue(value: any): void {
        if (value === null || value === undefined) return

        if (typeof value === 'object') {
            this.searchControl.setValue(value, { emitEvent: false })
            this.lastEmittedValue = this.returnObject
                ? value
                : this.identifierFn(value)
            return
        }

        const raw = String(value)
        const source =
            this.preloadedList.length > 0 ? this.preloadedList : this.items

        const matched = source.find(
            (item) => String(this.identifierFn(item)) === raw
        )

        if (matched) {
            this.searchControl.setValue(matched, { emitEvent: false })
            this.lastEmittedValue = this.returnObject
                ? matched
                : this.identifierFn(matched)
        } else {
            this.searchControl.setValue(raw, { emitEvent: false })
            this.lastEmittedValue = raw

            if (this.searchFn && typeof raw === 'string' && raw.length > 0) {
                this.loading = true
                this.searchFn(raw).subscribe((result) => {
                    this.items = Array.isArray(result) ? result : [result]
                    this.loading = false

                    const found = this.items.find(
                        (item) => String(this.identifierFn(item)) === raw
                    )

                    if (found) {
                        this.searchControl.setValue(found, { emitEvent: false })
                        this.lastEmittedValue = this.returnObject
                            ? found
                            : this.identifierFn(found)
                        this.itemSeleccionado.emit(found)
                        this.onChange(
                            this.returnObject ? found : this.identifierFn(found)
                        )
                    }
                })
            }
        }
    }

    registerOnChange(fn: (value: string | number) => void): void {
        this.onChange = fn
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled
        isDisabled ? this.searchControl.disable() : this.searchControl.enable()
    }
}
